import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Loader2, Zap, Sparkles, Check, TrendingUp } from "lucide-react";
import Header from "@/components/Header";
import AuthModal from "@/components/AuthModal";
import { apiClient } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { capture } from "@/lib/analytics";
import { getAnonId } from "@/lib/visitTracker";
import { BELTS } from "@/components/BeltSystem";
import { detectPersona, setPersona, PERSONA_COPY } from "@/lib/persona";

const STORAGE_KEY = "pending_skill_check_id";

/**
 * Skill Check — anonymous raw-skill prompt assessment.
 *
 * Flow: intro → assessment → grading → result + coaching → rewrite (Apply) →
 * improved score → choose path
 *
 * Design rules:
 * - The scenario is randomized server-side on every visit so users don't all
 *   see the same task.
 * - The textarea is always blank — only the placeholder is shown.
 * - Coaching, examples, and "stronger version" content only appear AFTER
 *   the score (never before).
 * - After the score, users see Key Lessons → Better Example → a Rewrite
 *   prompt input → re-score with delta so they can apply the lesson.
 */
export default function SkillCheck() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  // Flow: persona → assessment → grading → result. Persona selection
  // (REC-e14b7956c676) is a one-tap pre-step that adapts every downstream
  // surface (scenario prompt, result headline, result CTA) so the user
  // sees content grounded in the domain they actually care about.
  // Returning visitors who already chose a persona this session skip
  // straight to the assessment.
  const initialPersona = (() => {
    try { return sessionStorage.getItem("skill_check_persona"); } catch (_e) { return null; }
  })();
  // Show the intro card once per session — after persona pick, before
  // the textarea. Users returning from an incomplete session skip it.
  const initialIntroSeen = (() => {
    try { return sessionStorage.getItem("skill_check_intro_seen") === "1"; } catch (_e) { return false; }
  })();
  const _initialStep = !initialPersona
    ? "persona"
    : !initialIntroSeen
      ? "intro"
      : "assessment";
  const [step, setStep] = useState(_initialStep);
  const [selectedPersona, setSelectedPersona] = useState(initialPersona || null);
  const [task, setTask] = useState(null);
  const [promptText, setPromptText] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingMode, setPendingMode] = useState(null);

  useEffect(() => {
    capture("skill_check_started");
    apiClient.get("/skill-check/questions")
      .then((r) => {
        const serverTask = r.data?.task;
        // If the visitor picked a persona, overlay the persona-specific
        // scenario onto the server task. The `null` fallback keeps the
        // existing randomized scenario for the "professional / writing"
        // default so we don't regress the existing content.
        const persona = (() => {
          try { return sessionStorage.getItem("skill_check_persona"); } catch (_e) { return null; }
        })();
        const override = persona ? PERSONA_COPY.scenario_prompt[persona] : null;
        if (override && serverTask) {
          // The task's visible copy is `instruction` + `title`. We overlay
          // both so the persona-picked scenario becomes the challenge.
          setTask({
            ...serverTask,
            title: PERSONA_COPY.selector_label[persona] || serverTask.title,
            instruction: override,
            _persona_override: persona,
          });
        } else {
          setTask(serverTask);
        }
      })
      .catch((e) => setError(e?.message || "Couldn't load the assessment task"));
  }, []);

  // Persona selector handler — persist + advance the flow + swap scenario.
  const handlePersonaPick = (p) => {
    setPersona(p);            // writes sessionStorage `skill_check_persona`
    setSelectedPersona(p);
    capture("persona_selected", { persona: p });
    // Overlay the persona-specific scenario onto the currently-loaded task
    // if we have one. Falls through to the server default for
    // 'professional' (its scenario_prompt entry is intentionally null).
    const override = PERSONA_COPY.scenario_prompt[p];
    if (override) {
      setTask((prev) => (prev ? {
        ...prev,
        title: PERSONA_COPY.selector_label[p] || prev.title,
        instruction: override,
        _persona_override: p,
      } : prev));
    }
    // Persona → intro → assessment. Intro is a one-time-per-session
    // process-explainer; it does NOT coach the user before submission.
    setStep("intro");
  };

  const handleIntroContinue = () => {
    try { sessionStorage.setItem("skill_check_intro_seen", "1"); } catch (_e) { /* ignore */ }
    capture("skill_check_intro_dismissed");
    setStep("assessment");
  };

  // Fire drop-off analytics if the user closes the tab before seeing their result.
  useEffect(() => {
    const handler = () => {
      if (step !== "result") capture("user_dropped_before_completion", { last_step: step });
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [step]);

  const submitAssessment = async () => {
    const text = promptText.trim();
    if (text.length < 10) {
      setError("Give it a real shot — try at least one full sentence.");
      return;
    }
    setError(null);
    setStep("grading");
    try {
      const r = await apiClient.post("/skill-check/grade", {
        anon_id: getAnonId() || `anon-${Date.now()}`,
        user_prompt: text,
        scenario_id: task?.id || null,
      });
      setResult(r.data);
      try { localStorage.setItem(STORAGE_KEY, r.data.skill_check_id); } catch (err) { console.warn("Persist skill check id failed:", err); }
      capture("skill_check_completed", { scenario_id: task?.id });
      capture("prompt_score_generated", { score: r.data.score, belt: r.data.belt.slug });
      setStep("result");
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Couldn't grade your prompt. Try again?");
      setStep("assessment");
    }
  };

  const choose = async (mode) => {
    capture(mode === "choose_for_me" ? "choose_for_me_clicked" : "build_my_own_path_clicked", {
      score: result?.score,
    });
    if (!user) {
      setPendingMode(mode);
      setAuthOpen(true);
      return;
    }
    await claimAndRoute(mode);
  };

  const claimAndRoute = async (mode) => {
    try {
      const r = await apiClient.post("/skill-check/claim", {
        skill_check_id: result.skill_check_id,
        mode,
      });
      try { localStorage.removeItem(STORAGE_KEY); } catch (err) { console.warn("Drop skill check id failed:", err); }
      await refresh?.();
      capture("account_created_after_skill_check", { mode, score: result.score });
      if (mode === "choose_for_me") {
        const first = r.data?.recommended_path?.[0]?.first_lesson_id
          || result.recommended_path?.[0]?.first_lesson_id;
        navigate(first ? `/lesson/${first}` : "/dashboard");
      } else {
        navigate("/onboarding");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Couldn't save your skill check. Please sign up again.");
    }
  };

  useEffect(() => {
    if (user && pendingMode && result?.skill_check_id) {
      const mode = pendingMode;
      Promise.resolve().then(() => {
        setPendingMode(null);
        setAuthOpen(false);
        claimAndRoute(mode);
      });
    }
  }, [user, pendingMode, result]);

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <AnimatePresence mode="wait">
          {step === "persona" && (
            <Step key="persona">
              <PersonaSelectorStep onPick={handlePersonaPick} onBack={() => navigate("/")} />
            </Step>
          )}
          {step === "intro" && (
            <Step key="intro">
              <PromptExerciseIntroStep onContinue={handleIntroContinue} />
            </Step>
          )}
          {step === "assessment" && !task && (
            <Step key="assessment-loading">
              <div className="text-center py-16" data-testid="skill-check-loading">
                <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--primary,#2563EB)]" />
                <p className="text-sm text-[var(--ink-2)]">Loading your challenge…</p>
              </div>
            </Step>
          )}
          {step === "assessment" && task && (
            <Step key="assessment">
              <AssessmentStep
                task={task}
                value={promptText}
                onChange={setPromptText}
                onSubmit={submitAssessment}
                onBack={() => navigate("/")}
                error={error}
              />
            </Step>
          )}
          {step === "grading" && (
            <Step key="grading">
              <GradingStep />
            </Step>
          )}
          {step === "result" && result && (
            <Step key="result">
              <ResultStep result={result} onChoose={choose} hasUser={!!user} />
            </Step>
          )}
        </AnimatePresence>
      </main>
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} initialMode="signup" />
    </div>
  );
}

// ----------------- persona selector (pre-assessment) -----------------
// One-tap pre-step: "What do you want to use AI for?". Adapts the scenario
// prompt (this file's fetch), the result-screen headline + CTA (ResultStep),
// and the landing hero (via detectPersona() reading the same sessionStorage
// key). Analytics: fires `persona_selected` with the picked value.

// ----------------- prompt-exercise intro (pre-assessment) -----------------
// Short process-explainer between persona pick and the textarea.
// STRICT rule (operator direction 2026-07-06): must NOT coach the user
// how to write a better prompt before they submit. Explain the FLOW only.
function PromptExerciseIntroStep({ onContinue }) {
  return (
    <div data-testid="skill-check-intro" className="max-w-xl mx-auto text-center">
      <div
        className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-4"
        style={{ background: "rgba(37,99,235,0.10)", color: "var(--primary, #2563EB)" }}
      >
        <Sparkles size={11} /> The AI Prompt Exercise
      </div>
      <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight" data-testid="skill-check-intro-headline">
        Show Us How You&rsquo;d Prompt AI Today
      </h1>
      <p className="mt-3 text-base text-[var(--ink-2)] leading-relaxed" data-testid="skill-check-intro-body">
        Write the prompt you would naturally give AI for a real-world task. Prompt Dojo will review your answer, show your current skill rating, and recommend what to sharpen first.
      </p>

      <ol
        className="mt-8 text-left mx-auto max-w-md space-y-3"
        data-testid="skill-check-intro-steps"
      >
        {[
          "Write one prompt",
          "Get your skill rating",
          "See what to improve",
        ].map((label, idx) => (
          <li
            key={idx}
            className="flex items-start gap-3 rounded-xl p-3 border"
            style={{ background: "var(--bg-1,#fff)", borderColor: "var(--line)" }}
          >
            <span
              className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold"
              style={{ background: "rgba(37,99,235,0.10)", color: "var(--primary, #2563EB)" }}
            >
              {idx + 1}
            </span>
            <span className="text-sm text-[var(--ink-1)] leading-relaxed pt-0.5">{label}</span>
          </li>
        ))}
      </ol>

      <button
        type="button"
        onClick={onContinue}
        className="btn-duo mt-8 inline-flex items-center justify-center gap-2 px-6 py-3"
        data-testid="skill-check-intro-cta"
      >
        Start the Exercise <ArrowRight size={15} />
      </button>
    </div>
  );
}


function PersonaSelectorStep({ onPick, onBack }) {
  const CHOICES = [
    { id: "sales",        label: "Sales & Outreach",  hint: "Cold emails, follow-ups, pitch decks",             emoji: "📈" },
    { id: "professional", label: "Writing & Content", hint: "Emails, docs, briefs, everyday knowledge work",    emoji: "✍️" },
    { id: "builder",      label: "Building & Code",   hint: "APIs, JSON schemas, technical prompts",            emoji: "🛠️" },
    { id: "student",      label: "School & Research", hint: "Essays, study help, complex topics simplified",    emoji: "🎓" },
  ];
  return (
    <div data-testid="skill-check-persona-selector">
      <div className="text-center max-w-2xl mx-auto">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-4"
          style={{ background: "rgba(37,99,235,0.10)", color: "var(--primary, #2563EB)" }}
        >
          <Sparkles size={11} /> One quick step
        </div>
        <h1 className="font-display text-3xl sm:text-4xl leading-tight tracking-tight">
          What do you want to use AI for?
        </h1>
        <p className="mt-3 text-base text-[var(--ink-2)] leading-relaxed max-w-lg mx-auto">
          Pick the one closest to your day-to-day. We&rsquo;ll show you a challenge
          that actually matches your work.
        </p>
      </div>

      <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto">
        {CHOICES.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => onPick(c.id)}
            className="text-left rounded-2xl p-5 border transition-all hover:scale-[1.01] hover:shadow-lg"
            style={{ borderColor: "var(--line)", background: "var(--bg-1, #fff)" }}
            data-testid={`persona-choice-${c.id}`}
            data-track={`persona-select-${c.id}`}
          >
            <div className="flex items-start gap-3">
              <span className="text-2xl leading-none">{c.emoji}</span>
              <div className="min-w-0">
                <div className="font-display text-lg tracking-tight">{c.label}</div>
                <div className="text-xs text-[var(--ink-3)] mt-1 leading-snug">{c.hint}</div>
              </div>
              <ArrowRight size={16} className="text-[var(--ink-3)] ml-auto mt-1 flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>

      <div className="mt-6 text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] underline underline-offset-4"
          data-testid="skill-check-persona-back"
        >
          Back to home
        </button>
      </div>
    </div>
  );
}


// ----------------- shared wrapper -----------------
function Step({ children }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -12 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

// ----------------- assessment (NO pre-submit coaching) -----------------
function AssessmentStep({ task, value, onChange, onSubmit, onBack, error }) {
  const chars = value.length;
  return (
    <div data-testid="skill-check-assessment">
      <div className="text-xs font-bold uppercase tracking-[0.18em] text-[var(--ink-3)] mb-2">
        AI Skill Challenge · {task.title}
      </div>
      <h2 className="font-display text-2xl sm:text-3xl leading-tight tracking-tight mb-3">
        {task.instruction}
      </h2>
      <p className="text-sm text-[var(--ink-3)] leading-relaxed mb-5">
        There&rsquo;s no &ldquo;correct&rdquo; format. Write the prompt the way
        you would actually type it into AI.
      </p>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={7}
        maxLength={1500}
        placeholder={task.input_placeholder || "Write your prompt here…"}
        className="brut-input w-full font-mono text-sm leading-relaxed resize-y"
        data-testid="skill-check-prompt-input"
      />
      <div className="mt-2 flex items-center justify-end text-xs text-[var(--ink-3)]">
        <span>{chars} / 1500</span>
      </div>
      {error && (
        <div className="mt-3 text-sm" style={{ color: "var(--accent,#FF5C2A)" }} data-testid="skill-check-assessment-error">
          {error}
        </div>
      )}
      <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
        <button onClick={onBack} className="btn-ghost inline-flex items-center gap-1.5 text-sm" data-testid="skill-check-assessment-back">
          <ArrowLeft size={13} /> Back
        </button>
        <button
          onClick={onSubmit}
          disabled={chars < 10}
          className="btn-duo inline-flex items-center justify-center gap-2 px-6 py-3"
          data-testid="skill-check-submit"
        >
          {task.submit_label || "Submit My Prompt"} <ArrowRight size={15} />
        </button>
      </div>
    </div>
  );
}

// ----------------- grading -----------------
function GradingStep() {
  return (
    <div className="text-center py-16" data-testid="skill-check-grading">
      <Loader2 size={32} className="animate-spin mx-auto mb-4 text-[var(--primary,#2563EB)]" />
      <h2 className="font-display text-2xl tracking-tight">Grading your prompt…</h2>
      <p className="text-sm text-[var(--ink-2)] mt-2">Claude is reading it across all six categories.</p>
    </div>
  );
}

// ----------------- score badge (reused for original + improved) -----------------
function ScoreBadge({ score, belt, label }) {
  const beltMeta = BELTS.find((b) => b.id === belt?.slug) || BELTS[0];
  return (
    <div
      className="brut p-5 sm:p-6"
      style={{
        background: `linear-gradient(135deg, ${beltMeta.color}22 0%, #FFFFFF 70%)`,
        borderColor: `${beltMeta.color}66`,
      }}
    >
      <div className="flex items-center gap-4 flex-wrap">
        <div className="font-display text-4xl leading-none tracking-tight">
          {score}<span className="text-[var(--ink-3)] text-lg">/100</span>
        </div>
        <div
          className="px-3 py-1.5 rounded-lg font-bold text-xs tracking-wider"
          style={{ background: beltMeta.color, color: beltMeta.textOn }}
        >
          {belt?.label}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-bold">{label || "Prompt score"}</div>
          <div className="font-display text-base leading-tight mt-0.5">{belt?.title}</div>
        </div>
      </div>
    </div>
  );
}

// ----------------- result = "Your AI Growth Path Is Ready" hook screen -----------------
/**
 * After the user submits the Skill Check, the result screen is the strongest
 * hook moment in the app — the place they decide "this is the program I need
 * to keep using". So we frame it as a personalized growth-path reveal, not a
 * grading report:
 *
 *   - Your starting score + identity (belt)
 *   - What this means (one sentence, band-aware)
 *   - Your first skill to build (top opportunity, distilled from coaching)
 *   - A few quick rules (key lessons — kept, short list)
 *   - Your path based on your goals (recommended path, re-labeled as a journey)
 *   - Daily promise (return reason)
 *   - Today's first win (single primary CTA → first lesson)
 *
 * Removed vs. the old result screen:
 *   - "Now Try Again" rewrite input + re-grade loop (moved into lessons —
 *     this screen should NOT feel like another grading tool).
 *   - "Hint Examples" snippet grid (those still appear inside lessons).
 *   - The dual "Choose for Me / Build My Own" 50/50 card grid (replaced with
 *     a single CTA + secondary text link).
 */

function bandInterpretation(score) {
  if (score < 31) {
    return "You're just getting started with AI. We'll help you learn how to give clear instructions so AI gives better answers.";
  }
  if (score < 56) {
    return "You know what you want AI to do — you just need more practice giving clear details so AI gives better answers.";
  }
  if (score < 76) {
    return "You're getting good at writing prompts. We'll help you go deeper with structure, examples, and small details that matter.";
  }
  return "You already write strong prompts. We'll help you sharpen advanced skills for real-world projects.";
}

// Default growth-path bullets shown to anonymous users (badges aren't picked
// yet at this point in the flow — the dashboard later swaps in badge-aware
// copy). Kept short, kid-friendly, and outcome-focused.
const DEFAULT_GROWTH_BULLETS = [
  "Save time by giving AI clearer instructions",
  "Turn messy tasks into clear, useful answers",
  "Use AI to make better real-life decisions",
  "Build prompts you can reuse every day",
];

function ResultStep({ result, onChoose, hasUser }) {
  const coaching = result.coaching || {};
  const firstSkill = (coaching.how_to_improve || [])[0]
    || "Give AI clearer instructions so it gives better answers.";
  const firstLessonLabel = result.recommended_path?.[0]?.label
    || "Today's first skill";
  // Persona-adaptive headline + CTA per REC-cb6792d97925 / REC-b64aa57fe141.
  // Same detection util as the landing hero — a returning visitor with an
  // explicit persona choice via /skill-check will already have it stored.
  const persona = detectPersona();
  const resultHeadline = PERSONA_COPY.result_headline[persona]
    || PERSONA_COPY.result_headline.professional;
  const resultCta = PERSONA_COPY.result_cta[persona]
    || PERSONA_COPY.result_cta.professional;

  return (
    <div data-testid="skill-check-result" data-persona={persona}>
      {/* ───────── HERO ───────── */}
      <div className="text-center">
        <div
          className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] mb-4"
          style={{ background: "rgba(37,99,235,0.10)", color: "var(--primary, #2563EB)" }}
          data-testid="skill-check-hook-kicker"
        >
          <Sparkles size={11} /> {resultHeadline}
        </div>
        <h1
          className="font-display text-3xl sm:text-4xl leading-tight tracking-tight max-w-xl mx-auto"
          data-testid="skill-check-result-headline"
        >
          We found your starting point.
        </h1>
        <p className="mt-3 text-base text-[var(--ink-2)] leading-relaxed max-w-lg mx-auto">
          Now we&rsquo;ll help you build one useful AI skill at a time.
        </p>
      </div>

      {/* ───────── 1. Starting score + identity ───────── */}
      <div className="mt-8" data-testid="skill-check-starting-score">
        <ScoreBadge score={result.score} belt={result.belt} label="Your starting score" />
      </div>

      {/* ───────── 2. What this means ───────── */}
      <div
        className="mt-5 brut p-5 sm:p-6"
        style={{ background: "rgba(16,185,129,0.06)", borderColor: "rgba(16,185,129,0.35)" }}
        data-testid="skill-check-what-this-means"
      >
        <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-bold mb-2">
          What this means
        </div>
        <p className="text-sm sm:text-base text-[var(--ink)] leading-relaxed">
          {coaching.completion_message
            ? `${coaching.completion_message} ${bandInterpretation(result.score)}`
            : bandInterpretation(result.score)}
        </p>
      </div>

      {/* ───────── 3. Your first skill to build ───────── */}
      <div
        className="mt-5 brut p-5 sm:p-6"
        style={{ background: "rgba(255,92,42,0.05)", borderColor: "rgba(255,92,42,0.35)" }}
        data-testid="skill-check-first-skill"
      >
        <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-bold mb-2">
          Your first skill to build
        </div>
        <p className="text-base sm:text-lg text-[var(--ink)] leading-relaxed font-medium">
          {firstSkill}
        </p>
      </div>

      {/* ───────── 4. A few quick rules (key lessons) ───────── */}
      {coaching.key_lessons?.length > 0 && (
        <section className="mt-8" data-testid="skill-check-key-lessons">
          <h3 className="font-display text-xl sm:text-2xl tracking-tight leading-tight">
            A Few Quick Rules
          </h3>
          <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
            These small rules make almost any prompt better.
          </p>
          <ul className="mt-4 space-y-2.5">
            {coaching.key_lessons.slice(0, 3).map((lesson, i) => (
              <li
                key={`lesson-${i}`}
                className="flex items-start gap-3 brut p-4"
                data-testid={`skill-check-lesson-${i}`}
              >
                <div className="w-6 h-6 rounded-full bg-[var(--primary,#2563EB)] text-white flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">
                  {i + 1}
                </div>
                <p className="text-sm sm:text-base text-[var(--ink)] leading-relaxed">
                  {lesson}
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ───────── 5. Your path based on your goals ───────── */}
      <section className="mt-8" data-testid="skill-check-growth-path">
        <h3 className="font-display text-xl sm:text-2xl tracking-tight leading-tight">
          Your path based on your goals
        </h3>
        <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
          Here&rsquo;s what we&rsquo;ll help you learn:
        </p>
        <ul className="mt-4 space-y-2">
          {DEFAULT_GROWTH_BULLETS.map((b, i) => (
            <li
              key={`growth-${i}`}
              className="flex items-start gap-2.5 text-sm sm:text-base text-[var(--ink)] leading-relaxed"
              data-testid={`skill-check-growth-${i}`}
            >
              <Check size={15} className="text-[var(--emerald,#10B981)] flex-shrink-0 mt-1" strokeWidth={2.5} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* ───────── 6. Daily promise ───────── */}
      <div
        className="mt-8 brut p-5 sm:p-6"
        style={{
          background: "linear-gradient(135deg, rgba(37,99,235,0.05), rgba(124,58,237,0.05))",
          borderColor: "rgba(37,99,235,0.3)",
        }}
        data-testid="skill-check-daily-promise"
      >
        <div className="flex items-start gap-3">
          <TrendingUp size={20} className="text-[var(--primary,#2563EB)] flex-shrink-0 mt-0.5" strokeWidth={2.4} />
          <p className="text-sm sm:text-base text-[var(--ink)] leading-relaxed">
            <span className="font-semibold">Come back each day</span> to unlock
            one small AI skill that makes you faster, smarter, and more
            confident using AI.
          </p>
        </div>
      </div>

      {/* ───────── 7. Today's first win + CTA ───────── */}
      <section className="mt-8" data-testid="skill-check-first-win">
        <h3 className="font-display text-2xl sm:text-3xl tracking-tight leading-tight">
          Today&rsquo;s first win
        </h3>
        <div
          className="mt-4 brut p-5 sm:p-6"
          style={{ borderColor: "var(--ink)" }}
        >
          <div className="text-xs uppercase tracking-wider text-[var(--ink-3)] font-bold mb-2">
            Skill 1
          </div>
          <p className="text-base sm:text-lg text-[var(--ink)] leading-relaxed font-medium">
            {firstLessonLabel}
          </p>
          <p className="text-sm text-[var(--ink-2)] mt-2 leading-relaxed">
            One short challenge. You&rsquo;ll write a prompt, get simple
            feedback, and finish with a useful new skill.
          </p>
          <button
            onClick={() => onChoose("choose_for_me")}
            className="btn-duo mt-5 inline-flex items-center justify-center gap-2 px-6 py-3 w-full sm:w-auto"
            data-testid="skill-check-start-first-skill"
          >
            <Zap size={15} /> {resultCta} <ArrowRight size={15} />
          </button>
        </div>
        {/* Secondary path — quieter so the primary CTA stays the focus. */}
        <div className="mt-4 text-center">
          <button
            onClick={() => onChoose("build_my_own")}
            className="text-sm text-[var(--ink-3)] hover:text-[var(--ink)] underline underline-offset-4 transition-colors"
            data-testid="skill-check-pick-goals"
          >
            Or pick your own goals first
          </button>
        </div>
      </section>
    </div>
  );
}
